/**
 * The interface: clicks and hovers, paper windows (a rustle that fits the ink-wash screens) and the
 * HUD menu, tabs, the error buzz, quests, the level and realm-up fanfares (a gong and a zither
 * strum), training chimes and toggles. The HUD plays these through `uiSfx` (sfx/api.ts).
 */
import { registerSfx } from '../registry';
import { chime, gong, note, paper, ROOT_D, strum, woodblock } from '../voices';

registerSfx('ui.click', { label: 'คลิกปุ่ม', cat: 'ui', vary: 0.04, gap: 0.03 }, (k) => {
  // A small wooden tick.
  woodblock(k, { f: 1500, vol: 0.14 });
});

registerSfx('ui.hover', { label: 'ชี้ปุ่ม', cat: 'ui', vary: 0.06, gap: 0.05, gain: 0.6 }, (k) => {
  k.tone({ f: 2600, dur: 0.03, vol: 0.03, attack: 0.002 });
});

registerSfx('ui.paper.open', { label: 'เปิดหน้าต่างกระดาษ', cat: 'ui', vary: 0.05, gap: 0.08 }, (k) => {
  // A scroll unrolling: crackle of paper and a soft slide.
  paper(k, { dur: 0.32, vol: 0.22 });
  k.noise({ color: 'pink', dur: 0.3, vol: 0.08, attack: 0.1, type: 'lowpass', f: 1200, f1: 2400 });
});

registerSfx('ui.paper.close', { label: 'ปิดหน้าต่างกระดาษ', cat: 'ui', vary: 0.05, gap: 0.08 }, (k) => {
  paper(k, { dur: 0.2, vol: 0.18, bright: 0.8 });
  k.thump({ f0: 200, f1: 120, at: 0.17, dur: 0.06, vol: 0.12 });
});

registerSfx('ui.menu.open', { label: 'เปิดเมนู', cat: 'ui', vary: 0.02, gap: 0.08 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 0), dur: 0.5, vol: 0.12, bright: 0.7 });
  k.pluck({ f: note(ROOT_D * 2, 2), at: 0.05, dur: 0.6, vol: 0.12, bright: 0.7 });
});

registerSfx('ui.menu.close', { label: 'ปิดเมนู', cat: 'ui', vary: 0.02, gap: 0.08 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 2), dur: 0.4, vol: 0.1, bright: 0.6 });
  k.pluck({ f: note(ROOT_D * 2, 0), at: 0.05, dur: 0.5, vol: 0.1, bright: 0.6 });
});

registerSfx('ui.tab', { label: 'สลับแท็บ', cat: 'ui', vary: 0.04, gap: 0.05 }, (k) => {
  paper(k, { dur: 0.08, vol: 0.12 });
  k.pluck({ f: note(ROOT_D * 2, 4), dur: 0.35, vol: 0.06, bright: 0.8 });
});

registerSfx('ui.error', { label: 'ผิดพลาด', cat: 'ui', vary: 0, gap: 0.2 }, (k) => {
  // A low, dull double buzz.
  const lp = k.filter('lowpass', 900, 1);
  for (const at of [0, 0.13]) {
    k.tone({ type: 'square', f: 138, at, dur: 0.1, vol: 0.06, out: lp, linear: true });
    k.tone({ type: 'square', f: 146, at, dur: 0.1, vol: 0.05, out: lp, linear: true });
  }
});

registerSfx('ui.notice', { label: 'แจ้งเตือน', cat: 'ui', vary: 0.03, gap: 0.4 }, (k) => {
  woodblock(k, { f: 1100, vol: 0.1 });
  woodblock(k, { f: 1400, at: 0.08, vol: 0.07 });
});

registerSfx('ui.toggle.on', { label: 'เปิดสวิตช์', cat: 'ui', vary: 0, gap: 0.1 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 2), dur: 0.3, vol: 0.09 });
  k.pluck({ f: note(ROOT_D * 2, 4), at: 0.06, dur: 0.4, vol: 0.09 });
});

registerSfx('ui.toggle.off', { label: 'ปิดสวิตช์', cat: 'ui', vary: 0, gap: 0.1 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 4), dur: 0.3, vol: 0.08, bright: 0.4 });
  k.pluck({ f: note(ROOT_D * 2, 1), at: 0.06, dur: 0.4, vol: 0.08, bright: 0.4 });
});

// ───────── quests ─────────

registerSfx('ui.quest.accept', { label: 'รับภารกิจ', cat: 'ui', vary: 0, gap: 0.3, preview: 1.4 }, (k) => {
  // A brush stroke signing the scroll, and two notes up.
  k.noise({ color: 'pink', dur: 0.25, vol: 0.16, attack: 0.08, f: 900, f1: 2200, q: 1.4 });
  strum(k, ROOT_D, [2, 4], { at: 0.15, gap: 0.09, vol: 0.13 });
});

registerSfx('ui.quest.progress', { label: 'ภารกิจคืบหน้า', cat: 'ui', vary: 0.02, gap: 0.3 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 2), dur: 0.6, vol: 0.08 });
});

registerSfx('ui.quest.ready', { label: 'ภารกิจพร้อมส่ง', cat: 'ui', vary: 0, gap: 0.3 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 2), dur: 0.6, vol: 0.1 });
  k.pluck({ f: note(ROOT_D * 2, 4), at: 0.1, dur: 0.8, vol: 0.1 });
  chime(k, { f: note(ROOT_D * 4, 0), at: 0.2, vol: 0.04 });
});

registerSfx(
  'ui.quest.complete',
  { label: 'ภารกิจสำเร็จ', cat: 'ui', vary: 0, gap: 0.5, preview: 2.4 },
  (k) => {
    strum(k, ROOT_D, [0, 1, 2, 3, 4, 5], { gap: 0.05, vol: 0.12, dur: 1.6 });
    gong(k, { f: 196, at: 0.3, dur: 1.8, vol: 0.06 });
  },
);

// ───────── growth ─────────

registerSfx('ui.trained', { label: 'ฝึกวิชาขึ้นขั้น', cat: 'ui', vary: 0, gap: 0.3, preview: 1.4 }, (k) => {
  [0, 2, 4, 5].forEach((d, i) =>
    k.pluck({ f: note(ROOT_D * 2, d), at: i * 0.08, dur: 1, vol: 0.1, bright: 0.7 }),
  );
  chime(k, { f: note(ROOT_D * 4, 2), at: 0.32, vol: 0.04 });
});

registerSfx('ui.trainTick', { label: 'ฝึกปรือได้แต้ม (เบา)', cat: 'ui', vary: 0.03, gap: 0.5 }, (k) => {
  chime(k, { f: note(ROOT_D * 4, k.pick([0, 2, 4])), vol: 0.025, dur: 0.8 });
});

registerSfx('ui.levelUp', { label: 'เลื่อนชั้น', cat: 'ui', vary: 0, gap: 0.5, preview: 2.6 }, (k) => {
  strum(k, ROOT_D, [0, 2, 4, 5, 7], { gap: 0.06, vol: 0.13, dur: 1.8 });
  chime(k, { f: note(ROOT_D * 4, 0), at: 0.32, vol: 0.05, dur: 1.4 });
  gong(k, { f: 220, at: 0.02, dur: 1.6, vol: 0.05 });
});

registerSfx(
  'ui.realmUp',
  { label: 'ทะลวงขอบเขต (เลื่อนภพ)', cat: 'ui', vary: 0, gap: 1, preview: 4 },
  (k) => {
    // The big one: a deep gong, then the zither sweeping up the whole scale twice and ringing out.
    gong(k, { f: 98, dur: 3.6, vol: 0.16 });
    strum(k, ROOT_D / 2, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], { at: 0.25, gap: 0.04, vol: 0.12, dur: 2 });
    strum(k, ROOT_D, [5, 6, 7, 8, 9, 10], { at: 0.75, gap: 0.05, vol: 0.1, dur: 2.2 });
    chime(k, { f: note(ROOT_D * 4, 0), at: 1.1, vol: 0.05, dur: 2 });
  },
);

registerSfx('ui.learnArt', { label: 'ได้รับวิชาใหม่', cat: 'ui', vary: 0, gap: 0.5, preview: 2.4 }, (k) => {
  gong(k, { f: 146.8, dur: 2, vol: 0.08 });
  strum(k, ROOT_D, [0, 2, 4, 7], { at: 0.2, gap: 0.07, vol: 0.12 });
});

registerSfx('ui.school', { label: 'เข้าสำนัก', cat: 'ui', vary: 0, gap: 1, preview: 3.2 }, (k) => {
  gong(k, { f: 110, dur: 2.8, vol: 0.13 });
  gong(k, { f: 110, at: 0.9, dur: 2.2, vol: 0.08 });
  strum(k, ROOT_D, [0, 2, 4], { at: 0.4, gap: 0.12, vol: 0.12, dur: 1.6 });
});

registerSfx('ui.title', { label: 'ได้รับฉายา', cat: 'ui', vary: 0, gap: 1, preview: 2 }, (k) => {
  strum(k, ROOT_D, [4, 5, 7, 9], { gap: 0.07, vol: 0.11 });
  chime(k, { f: note(ROOT_D * 4, 2), at: 0.3, vol: 0.05, dur: 1.2 });
});

registerSfx('ui.lose', { label: 'พ่ายแพ้', cat: 'ui', vary: 0, gap: 1, preview: 2 }, (k) => {
  [4, 2, 1, 0].forEach((d, i) =>
    k.pluck({ f: note(ROOT_D / 2, d), at: i * 0.16, dur: 1.2, vol: 0.12, bright: 0.35 }),
  );
});
